import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useContent } from 'src/contexts/ContentContext';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { AddCustomPaymentTypeDialog } from 'src/dialogs/AddCustomPaymentTypeDialog';
import { useGetCustomPaymentTypesQuery } from 'src/hooks/api/useGetCustomPaymentTypesQuery';
import { useInsertCustomPaymentTypeMutation } from 'src/hooks/api/useInsertCustomPaymentTypeMutation';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { ExternalPaymentMethodType, Feature } from 'src/WebApiController';

import { RetailSaleFormValues } from '../types';
import * as styles from './ExternalPaymentSection.css';

/**
 * Maps each ExternalPaymentMethodType numeric value (as string key) to its
 * human-readable label. Used as `valueOptionsContent` for PosSelect.
 */
const EXTERNAL_PAYMENT_METHOD_LABELS: Record<string, string> = {
  [String(ExternalPaymentMethodType.CreditCard)]: 'Credit Card',
  [String(ExternalPaymentMethodType.Wire)]: 'Wire',
  [String(ExternalPaymentMethodType.ACH)]: 'ACH',
  [String(ExternalPaymentMethodType.Check)]: 'Check',
  [String(ExternalPaymentMethodType.CustomerCredit)]: 'Customer Credit',
  [String(ExternalPaymentMethodType.Cash)]: 'Cash',
  [String(ExternalPaymentMethodType.Other)]: 'Other',
};

/** Prefix for custom payment type keys in the dropdown */
const CUSTOM_TYPE_PREFIX = 'custom_';

/** Sentinel key for the "Add New Type" option */
const ADD_NEW_KEY = '__add_new__';

/**
 * Returns the adaptive note placeholder based on the selected payment method.
 */
const getNotePlaceholder = (
  methodType: ExternalPaymentMethodType | null | undefined
): string => {
  switch (methodType) {
    case ExternalPaymentMethodType.CreditCard:
      return 'Card reference or last 4 digits';
    case ExternalPaymentMethodType.Wire:
      return 'Wire reference number';
    case ExternalPaymentMethodType.ACH:
      return 'ACH confirmation number';
    case ExternalPaymentMethodType.Check:
      return 'Check number';
    case ExternalPaymentMethodType.CustomerCredit:
      return 'Credit reference or notes';
    case ExternalPaymentMethodType.Cash:
      return 'Payment notes (optional)';
    case ExternalPaymentMethodType.Other:
      return 'Describe the payment method or reference';
    default:
      return 'Payment method or notes (optional)…';
  }
};

/**
 * Controlled props mode — used when the parent manages state via useState
 * (e.g. GroupMarketplacePaymentDialog which does not use react-hook-form).
 */
export type ExternalPaymentSectionControlledProps = {
  methodType: ExternalPaymentMethodType | undefined;
  note: string;
  date: string;
  currencyCode: string;
  customPaymentTypeName?: string;
  methodTypeError?: string;
  noteError?: string;
  dateError?: string;
  onMethodTypeChange: (value: ExternalPaymentMethodType | undefined) => void;
  onNoteChange: (value: string) => void;
  onDateChange: (value: string) => void;
  onCustomPaymentTypeNameChange?: (value: string | undefined) => void;
};

type ExternalPaymentSectionProps = {
  disabled: boolean;
  /**
   * The total sale amount (proceeds + line items). The external payment amount
   * is locked to this value in Step 1; partial payment support comes in Step 2.
   */
  totalAmount: number;
  /**
   * Optional controlled props. When provided the component operates in
   * "controlled mode" and does NOT call useFormContext. This allows the
   * component to be used outside of a react-hook-form FormProvider.
   */
  controlled?: ExternalPaymentSectionControlledProps;
};

/**
 * Renders the sub-form shown when the broker selects "Paid Externally".
 * Collects:
 * - Payment method type (required) — rendered as a dropdown
 * - Note (optional, max 500 chars) — placeholder adapts to selected method
 * - Amount (read-only, locked to sale total — partial payments via PartialPaymentInput)
 * - Date incurred (defaults to today, cannot be future)
 *
 * Supports two modes:
 * 1. **Form-context mode** (default): reads/writes via useFormContext<RetailSaleFormValues>.
 *    Used by RetailSaleCheckoutStep (react-hook-form FormProvider is present).
 * 2. **Controlled mode**: pass `controlled` prop. Used by GroupMarketplacePaymentDialog
 *    which manages external-payment fields via useState.
 */
export const ExternalPaymentSection = ({
  disabled,
  totalAmount,
  controlled,
}: ExternalPaymentSectionProps) => {
  // Call hooks unconditionally to satisfy the Rules of Hooks.
  // When `controlled` is provided, we ignore the form-context values.
  const formContext = useFormContext<RetailSaleFormValues>();
  const hasCustomExternalPaymentTypes = useUserHasFeature(
    Feature.CustomExternalPaymentTypes
  );
  const customTypesQuery = useGetCustomPaymentTypesQuery('Sale');
  const customPaymentTypes = customTypesQuery.data;
  const insertMutation = useInsertCustomPaymentTypeMutation('Sale');
  const addNewPaymentTypeLabel = useContent(ContentId.AddNewPaymentType);

  const [showAddDialog, setShowAddDialog] = useState(false);

  // Merge standard labels with custom types and "Add New" option
  const mergedLabels = useMemo(() => {
    const labels = { ...EXTERNAL_PAYMENT_METHOD_LABELS };
    if (hasCustomExternalPaymentTypes && customPaymentTypes) {
      for (const ct of customPaymentTypes) {
        labels[`${CUSTOM_TYPE_PREFIX}${ct.id}`] = ct.name;
      }
      labels[ADD_NEW_KEY] = addNewPaymentTypeLabel;
    }
    return labels;
  }, [
    hasCustomExternalPaymentTypes,
    customPaymentTypes,
    addNewPaymentTypeLabel,
  ]);

  const isControlled = controlled != null;

  // Track when a custom type is selected so the dropdown can show the custom name
  const [selectedCustomKey, setSelectedCustomKey] = useState<string | null>(
    null
  );

  // Form-context values (only used when NOT in controlled mode)
  const fcMethodType = formContext?.watch('externalPaymentMethodType');
  const fcDate = formContext?.watch('externalPaymentDate');
  const fcNote = formContext?.watch('externalPaymentNote');
  const fcCurrencyCode = formContext?.watch('currencyCode') ?? 'USD';
  const fcMethodTypeError =
    formContext?.formState?.errors?.externalPaymentMethodType?.message;
  const fcDateError =
    formContext?.formState?.errors?.externalPaymentDate?.message;
  const fcNoteError =
    formContext?.formState?.errors?.externalPaymentNote?.message;

  // Form-context mode: keep externalPaymentAmount in sync with the sale total.
  useEffect(() => {
    if (!isControlled && formContext) {
      formContext.setValue('externalPaymentAmount', totalAmount, {
        shouldValidate: false,
      });
    }
  }, [totalAmount, formContext, isControlled]);

  // Resolved values — pick controlled or form-context
  const methodType = isControlled ? controlled.methodType : fcMethodType;
  const dateStr = isControlled ? controlled.date : fcDate;
  const note = isControlled ? controlled.note : (fcNote ?? '');
  const currencyCode = isControlled ? controlled.currencyCode : fcCurrencyCode;
  const methodTypeError = isControlled
    ? controlled.methodTypeError
    : fcMethodTypeError;
  const dateError = isControlled ? controlled.dateError : fcDateError;
  const noteError = isControlled ? controlled.noteError : fcNoteError;

  const notePlaceholder = getNotePlaceholder(methodType);

  // Hoist today's date so it is not recalculated on every render.
  const maxDate = useMemo(() => new Date(), []);

  // Convert the stored ISO date string to a Date object for DatePickerInput.
  const dateValue = useMemo(
    () => (dateStr ? new Date(dateStr) : undefined),
    [dateStr]
  );

  const handleMethodTypeChange = (value: string) => {
    if (value === '') {
      if (isControlled) {
        controlled.onMethodTypeChange(undefined);
        controlled.onCustomPaymentTypeNameChange?.(undefined);
      } else {
        formContext?.setValue('externalPaymentMethodType', null, {
          shouldValidate: true,
        });
        formContext?.setValue('customPaymentTypeName', undefined, {
          shouldValidate: false,
        });
      }
      return;
    }

    // Intercept "Add New Type" selection
    if (value === ADD_NEW_KEY) {
      setShowAddDialog(true);
      return;
    }

    // Check if a custom type was selected
    if (value.startsWith(CUSTOM_TYPE_PREFIX)) {
      const customId = parseInt(value.substring(CUSTOM_TYPE_PREFIX.length), 10);
      const customName = customPaymentTypes?.find(
        (ct) => ct.id === customId
      )?.name;
      // For custom types, set method type to Other and pass customPaymentTypeName separately
      if (isControlled) {
        controlled.onMethodTypeChange(ExternalPaymentMethodType.Other);
        controlled.onCustomPaymentTypeNameChange?.(customName);
      } else {
        formContext?.setValue(
          'externalPaymentMethodType',
          ExternalPaymentMethodType.Other,
          { shouldValidate: true }
        );
        formContext?.setValue('customPaymentTypeName', customName, {
          shouldValidate: false,
        });
      }
      // Store the selected custom key so the dropdown displays the custom name
      setSelectedCustomKey(value);
      return;
    }

    // Standard type selected — clear custom key and customPaymentTypeName
    setSelectedCustomKey(null);
    const parsed = value as unknown as ExternalPaymentMethodType;
    if (isControlled) {
      controlled.onMethodTypeChange(parsed);
      controlled.onCustomPaymentTypeNameChange?.(undefined);
    } else {
      formContext?.setValue('externalPaymentMethodType', parsed, {
        shouldValidate: true,
      });
      formContext?.setValue('customPaymentTypeName', undefined, {
        shouldValidate: false,
      });
    }
  };

  const handleNoteChange = (value: string) => {
    if (isControlled) {
      controlled.onNoteChange(value);
    } else {
      formContext?.setValue('externalPaymentNote', value || null, {
        shouldValidate: true,
      });
    }
  };

  const handleDateChange = (date: Date) => {
    const isoDate = date.toISOString().split('T')[0];
    if (isControlled) {
      controlled.onDateChange(isoDate);
    } else {
      formContext?.setValue('externalPaymentDate', isoDate, {
        shouldValidate: true,
      });
    }
  };

  const handleAddCustomTypeSave = async (name: string) => {
    try {
      const newId = await insertMutation.mutateAsync({ name });
      setShowAddDialog(false);
      // Auto-select the newly created type.
      // Set the form values directly with the known `name` rather than calling
      // handleMethodTypeChange — the React Query cache hasn't refetched yet so
      // customPaymentTypes.find() would fail and produce an empty string.
      const newKey = `${CUSTOM_TYPE_PREFIX}${newId}`;
      setSelectedCustomKey(newKey);
      if (isControlled) {
        controlled.onMethodTypeChange(ExternalPaymentMethodType.Other);
        controlled.onCustomPaymentTypeNameChange?.(name);
      } else {
        formContext?.setValue(
          'externalPaymentMethodType',
          ExternalPaymentMethodType.Other,
          { shouldValidate: true }
        );
        formContext?.setValue('customPaymentTypeName', name, {
          shouldValidate: false,
        });
      }
    } catch {
      // Mutation error is handled by React Query
    }
  };

  return (
    <div className={styles.sectionContainer}>
      {/* Payment method type dropdown */}
      <PosFormField label="Payment Method Type *" errors={methodTypeError}>
        <PosSelect
          value={
            selectedCustomKey != null
              ? selectedCustomKey
              : methodType != null
                ? String(methodType)
                : undefined
          }
          placeholderText="Select payment method"
          valueOptionsContent={mergedLabels}
          sortMode="none"
          disabled={disabled}
          hasErrors={!!methodTypeError}
          showFullWidth
          onChange={handleMethodTypeChange}
        />
      </PosFormField>

      {/* Note */}
      <PosFormField label="Note" errors={noteError}>
        <PosTextArea
          placeholder={notePlaceholder}
          maxLength={500}
          disabled={disabled}
          value={note ?? ''}
          onChange={(e) => handleNoteChange(e.target.value)}
        />
      </PosFormField>

      {/* Amount — read-only, locked to the sale total (partial handled by PartialPaymentInput) */}
      <PosFormField label="Amount">
        <div className={styles.amountRow}>
          <span className={styles.currencyLabel}>{currencyCode}</span>
          <span>{formatCurrency(totalAmount, currencyCode)}</span>
        </div>
      </PosFormField>

      {/* Date incurred */}
      <PosFormField label="Date Incurred *" errors={dateError}>
        <DatePickerInput
          date={dateValue}
          maxDate={maxDate}
          disabled={disabled}
          onDateChange={handleDateChange}
        />
      </PosFormField>

      {/* Add Custom Payment Type Dialog */}
      <AddCustomPaymentTypeDialog
        isOpen={showAddDialog}
        onSave={handleAddCustomTypeSave}
        onCancel={() => setShowAddDialog(false)}
      />
    </div>
  );
};
